import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';
import { CONFIG } from '@/core/config';
import Toolbar from '@/ui/Toolbar';
import Canvas2D from '@/canvas/Canvas2D';
import ObjectLibrary from '@/ui/ObjectLibrary';
import PropertiesPanel from '@/ui/PropertiesPanel';
import StageMetadataPanel from '@/ui/StageMetadataPanel';
import StatusBar from '@/ui/StatusBar';
import Minimap from '@/ui/Minimap';
import CanvasErrorBoundary from '@/ui/CanvasErrorBoundary';
import ShortcutsOverlay from '@/ui/ShortcutsOverlay';
import { createDefaultLayout } from '@/stage/defaults';
import { smartSave } from '@/core/save';
import { v4 as uuidv4 } from 'uuid';

const Scene3D = lazy(() => import('@/scene/Scene3D'));

/**
 * Main designer layout
 * ┌─────────────────────────────────────────────────────┐
 * │ Toolbar                                              │
 * ├──────────┬──────────────────────────────┬───────────┤
 * │ Object   │                              │ Properties│
 * │ Library  │   Canvas2D / 3D Viewport     │ Panel     │
 * │ +        │                              │           │
 * │ Metadata │                              │           │
 * ├──────────┴──────────────────────────────┴───────────┤
 * │ Status Bar                                           │
 * └─────────────────────────────────────────────────────┘
 *
 * - Left panel: tabs for Object Library and Stage Metadata
 * - Center: Canvas2D component (or 3D scene when viewMode is '3d')
 * - Right panel: Properties (shows when object selected)
 * - Panels are collapsible
 * - Auto-save timer (30s interval to localStorage)
 * - Keyboard shortcut handler at this level
 */

export default function DesignerPage() {
  // --- Zustand selectors: only subscribe to the specific values needed for rendering ---
  const viewMode = useEditorStore((s) => s.viewMode);
  const isLeftPanelOpen = useEditorStore((s) => s.isLeftPanelOpen);
  const isRightPanelOpen = useEditorStore((s) => s.isRightPanelOpen);
  const hasSelection = useEditorStore((s) => s.selectedObjectIds.length > 0);
  const layout = useStageStore((s) => s.layout);
  const canvasConfig = useStageStore((s) => s.layout?.canvas);

  const [leftTab, setLeftTab] = useState<'objects' | 'metadata'>('objects');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Auto-save timer — reads fresh state via getState() each tick to avoid
  // re-creating the interval when store values change.
  useEffect(() => {
    const timer = setInterval(() => {
      const editor = useEditorStore.getState();
      const stage = useStageStore.getState();
      if (editor.isDirty && stage.layout) {
        try {
          localStorage.setItem('stage_layout_draft', JSON.stringify(stage.layout));
          editor.setDirty(false);
        } catch (error) {
          console.error('Failed to auto-save to localStorage:', error);
        }
      }
    }, CONFIG.AUTO_SAVE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  // Global keyboard shortcut handler — reads fresh state via getState() at
  // call time so the handler never goes stale and the effect never re-runs.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const editorState = useEditorStore.getState();
      const stageState = useStageStore.getState();
      const historyState = useHistoryStore.getState();

      // Ctrl/Cmd + Z = Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        const prevState = historyState.undo();
        if (prevState) {
          stageState.setLayout(prevState);
        }
        return;
      }

      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y = Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'y') && e.shiftKey) {
        e.preventDefault();
        const nextState = historyState.redo();
        if (nextState) {
          stageState.setLayout(nextState);
        }
        return;
      }

      // Ctrl/Cmd + S = Save (server if attached, JSON download if standalone)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        void smartSave();
        return;
      }

      // Delete/Backspace key = Delete selected objects
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const { selectedObjectIds } = editorState;
        if (selectedObjectIds.length > 0) {
          // Push pre-mutation state to history BEFORE deleting
          if (stageState.layout) {
            historyState.pushState(stageState.layout);
          }
          for (const id of selectedObjectIds) {
            stageState.removeObject(id);
          }
          editorState.deselectAll();
          editorState.setDirty(true);
        }
        return;
      }

      // Arrow keys = nudge selected objects (grid size if snap on, else 0.1 m)
      if (
        !e.ctrlKey && !e.metaKey && !e.altKey &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
        editorState.selectedObjectIds.length > 0
      ) {
        e.preventDefault();
        const step = editorState.snapToGrid ? editorState.gridSize : 0.1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        if (stageState.layout) historyState.pushState(stageState.layout);
        for (const id of editorState.selectedObjectIds) {
          const obj = stageState.layout?.objects.find((o) => o.id === id);
          if (obj) {
            stageState.updateObject(id, {
              position: { x: obj.position.x + dx, y: obj.position.y + dy, z: obj.position.z },
            });
          } else {
            const pos = stageState.layout?.shooting_positions.find((p) => p.id === id);
            if (pos) {
              stageState.updatePosition(id, {
                position: { x: pos.position.x + dx, y: pos.position.y + dy, z: pos.position.z },
              });
            }
          }
        }
        editorState.setDirty(true);
        return;
      }

      // Ctrl/Cmd + G = Group selected objects (Shift to ungroup)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        const { selectedObjectIds } = editorState;
        if (!stageState.layout || selectedObjectIds.length === 0) return;

        historyState.pushState(stageState.layout);
        if (e.shiftKey) {
          for (const id of selectedObjectIds) {
            stageState.updateObject(id, { group_id: undefined });
          }
        } else if (selectedObjectIds.length >= 2) {
          const gid = uuidv4();
          for (const id of selectedObjectIds) {
            stageState.updateObject(id, { group_id: gid });
          }
        }
        editorState.setDirty(true);
        return;
      }

      // Ctrl/Cmd + D = Duplicate selected objects
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        const { selectedObjectIds } = editorState;
        if (selectedObjectIds.length > 0 && stageState.layout) {
          historyState.pushState(stageState.layout);
          for (const id of selectedObjectIds) {
            const obj = stageState.layout.objects.find((o) => o.id === id);
            if (obj) {
              stageState.addObject({
                ...obj,
                id: uuidv4(),
                position: {
                  x: obj.position.x + 0.5,
                  y: obj.position.y + 0.5,
                  z: obj.position.z,
                },
              });
            }
          }
          editorState.setDirty(true);
        }
        return;
      }

      // ? opens the keyboard-shortcuts overlay. On most US layouts this is
      // Shift + /; we check key so we don't clash with the modifier combos above.
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setShortcutsOpen((v) => !v);
        return;
      }

      // Tool shortcuts (only when no modifier keys held)
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'v': editorState.setTool('select'); return;
          case 'p': editorState.setTool('place'); return;
          case 'm': editorState.setTool('move'); return;
          case 'r': editorState.setTool('rotate'); return;
          case 'c': editorState.setTool('scale'); return;
          case 'w': editorState.setTool('wall_draw'); return;
          case 'l': editorState.setTool('measure'); return;
          case 'g': editorState.toggleGrid(); return;
          case '`': editorState.toggleSnapToGrid(); return;
          case 'f':
            if (editorState.viewMode === '3d') editorState.toggleWalkthrough();
            return;
          case 'e':
            if (editorState.viewMode === '3d') editorState.toggleEditMode3D();
            return;
          case 'escape':
            // In 3D edit mode: first Escape cancels placement, second exits
            if (editorState.viewMode === '3d' && editorState.is3DEditMode) {
              if (editorState.placingObjectType) {
                editorState.setPlacing(null);
              } else {
                editorState.setEditMode3D(false);
              }
            }
            return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []); // Empty deps — handler reads fresh state via getState()

  // Initialize stage layout if not already loaded
  useEffect(() => {
    const stage = useStageStore.getState();
    if (!stage.layout) {
      // Try to load from localStorage
      const saved = localStorage.getItem('stage_layout_draft');
      if (saved) {
        try {
          stage.loadFromJson(saved);
        } catch (error) {
          console.error('Failed to load saved stage:', error);
          stage.setLayout(createDefaultLayout('Untitled Stage'));
        }
      } else {
        stage.setLayout(createDefaultLayout('Untitled Stage'));
      }
    }
  }, []);

  if (!layout) {
    return (
      <div className="flex items-center justify-center w-screen h-screen bg-background text-foreground">
        <div className="text-center">
          <div className="mb-4 text-lg font-semibold">Initializing Stage...</div>
          <div className="w-16 h-16 mx-auto border-4 border-muted rounded-full border-t-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-screen h-screen bg-background text-foreground overflow-hidden">
      <Toolbar />

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel: Object Library + Stage Metadata */}
        {isLeftPanelOpen && (
          <div className="w-[280px] border-r border-border flex flex-col bg-background overflow-hidden">
            <div className="flex border-b border-border">
              <button
                className={`flex-1 px-3 py-2 text-sm font-medium ${leftTab === 'objects' ? 'bg-accent text-accent-foreground border-b-2 border-primary' : 'text-muted-foreground hover:bg-accent/50'}`}
                onClick={() => setLeftTab('objects')}
              >Objects</button>
              <button
                className={`flex-1 px-3 py-2 text-sm font-medium ${leftTab === 'metadata' ? 'bg-accent text-accent-foreground border-b-2 border-primary' : 'text-muted-foreground hover:bg-accent/50'}`}
                onClick={() => setLeftTab('metadata')}
              >Stage Info</button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {leftTab === 'objects' ? <ObjectLibrary /> : <StageMetadataPanel />}
            </div>
          </div>
        )}

        {/* Center: 2D Canvas or 3D Scene */}
        <div className="flex-1 relative bg-secondary overflow-hidden">
          {viewMode === '2d' ? (
            <CanvasErrorBoundary label="2D canvas">
              <Canvas2D />
            </CanvasErrorBoundary>
          ) : (
            <CanvasErrorBoundary label="3D scene">
              <Suspense fallback={
                <div className="flex items-center justify-center w-full h-full">
                  <div className="text-sm text-muted-foreground">Loading 3D view...</div>
                </div>
              }>
                <Scene3D />
              </Suspense>
            </CanvasErrorBoundary>
          )}
          {viewMode === '2d' && canvasConfig && (
            <Minimap
              canvasWidth={canvasConfig.width_m}
              canvasHeight={canvasConfig.height_m}
              viewportWidth={800}
              viewportHeight={600}
            />
          )}
        </div>

        {/* Right Panel: Properties */}
        {isRightPanelOpen && hasSelection && (
          <div className="w-[280px] border-l border-border bg-background overflow-y-auto">
            <PropertiesPanel />
          </div>
        )}
      </div>

      <StatusBar />

      <ShortcutsOverlay open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
}
